import clsx from "clsx";
import React, { type ComponentType } from "react";
import { useIntl } from "react-intl";

// File-type glyphs are vendored Lucide icons (defined in the redesign icon module — the skill's Lucide
// home); this usage-level file only imports them.
import { FileArchive, FileCode, FileCog, FilePlay, Files, FileSpreadsheet, FileText } from "~/common/redesign-v1/icons";

import { ChatImagePreviewTrigger } from "./chat-image-preview";

export type ChatFileKind = "image" | "video" | "file";

function apiBasePath(apiRaw: string): string {
  const trimmed = apiRaw.trim().replace(/\/+$/, "");
  if (!trimmed) return "";
  if (trimmed.includes("://")) {
    try {
      return new URL(trimmed).pathname.replace(/\/+$/, "") || "";
    } catch {
      return "";
    }
  }
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function chatFileKind(mime: string | null | undefined, fileName: string): ChatFileKind {
  const m = (mime || "").toLowerCase();
  const n = fileName.toLowerCase();
  if (m.startsWith("image/")) return "image";
  if (m.startsWith("video/")) return "video";
  if (/\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(n)) return "image";
  if (/\.(mp4|webm|mov|mpe?g|ogg|mkv)$/i.test(n)) return "video";
  return "file";
}

/** Backend returns get_url like /api/v1/chat/files/{id}; API may be /api/v1 or https://host/api/v1. */
export function resolveChatFileUrl(getUrl: string | null | undefined): string | undefined {
  if (!getUrl) return undefined;
  if (getUrl.startsWith("http://") || getUrl.startsWith("https://")) return getUrl;

  const path = getUrl.startsWith("/") ? getUrl : `/${getUrl}`;
  const apiRaw = (process.env.API ?? "").trim().replace(/\/+$/, "");
  if (!apiRaw) return path;

  const basePath = apiBasePath(apiRaw);
  if (basePath && (path === basePath || path.startsWith(`${basePath}/`))) {
    if (apiRaw.includes("://")) {
      try {
        return `${new URL(apiRaw).origin}${path}`;
      } catch {
        return path;
      }
    }
    return path;
  }

  if (apiRaw.includes("://")) {
    return `${apiRaw}${path}`;
  }
  const base = basePath || apiRaw;
  return `${base}${path}`;
}

export function chatFileExtensionLabel(fileName: string): string {
  const base = fileName.split(/[/\\]/).pop() ?? fileName;
  const dot = base.lastIndexOf(".");
  if (dot <= 0 || dot === base.length - 1) return "file";
  const ext = base.slice(dot + 1).toLowerCase();
  return ext.length > 4 ? ext.slice(0, 4) : ext;
}

type FileGlyphKind = "grid" | "zip" | "gear" | "film" | "code" | "doc";

/** Which inner glyph the document-page shows for a given extension (frame 5291-126507). Exported so the
 * extension→glyph contract (the D5 "file icons full list") is unit-locked. */
export function fileGlyphKind(extension: string): FileGlyphKind {
  const ext = extension.toLowerCase();
  if (/^(stl|obj|ply|step|stp|xls|xlsx|csv)$/.test(ext)) return "grid";
  if (/^(zip|rar|7z|gz|tar)$/.test(ext)) return "zip";
  if (/^(exe|msi|app|dmg|bin)$/.test(ext)) return "gear";
  if (/^(mov|mp4|mpe?g|avi|mkv|webm|wmv)$/.test(ext)) return "film";
  if (/^(html?|xml|json|js|ts|css)$/.test(ext)) return "code";
  return "doc"; // pdf/doc/docx/txt/rtf/md/… — document lines
}

// The document-page glyph = the canonical Lucide `file-*` icon per kind (vendored — redesign skill: Lucide
// first). The frames draw a page with the folded corner + a kind-specific inner glyph, which is exactly
// what these Lucide icons are.
const FILE_GLYPH: Record<FileGlyphKind, ComponentType<{ className?: string }>> = {
  grid: FileSpreadsheet,
  zip: FileArchive,
  gear: FileCog,
  film: FilePlay,
  code: FileCode,
  doc: FileText,
};

/**
 * The file glyph the chat frames use for non-image/video files (5291-126507 / 5289-123835): the Lucide
 * `file-*` page-with-inner-glyph, muted grey, with a dark ".EXT" badge overlapping the bottom. `size`
 * scales it (grid tile vs the small inline row icon); pass `iconClassName`/`badgeClassName` to override
 * the sizing for a consumer with its own dimensions (e.g. the instructions page's 80×96 tile icon)
 * instead of growing the `size` enum per caller.
 */
export function FileGlyph({ ext, size = "grid", iconClassName, badgeClassName }: { ext: string; size?: "grid" | "inline"; iconClassName?: string; badgeClassName?: string }) {
  const Icon = FILE_GLYPH[fileGlyphKind(ext)];
  const badge = `.${ext.toUpperCase()}`;
  const iconCls = iconClassName ?? (size === "grid" ? "tw-size-11" : "tw-size-6");
  const badgeCls = badgeClassName ?? (size === "grid" ? "tw-bottom-1 tw-px-1.5 tw-py-0.5 tw-text-[11px]" : "-tw-bottom-0.5 tw-px-1 tw-text-[8px]");
  return (
    <span className="tw-relative tw-inline-flex tw-items-center tw-justify-center" aria-hidden>
      <Icon className={`${iconCls} tw-text-[#A8A29E]`} />
      <span className={`tw-absolute tw-left-1/2 tw-max-w-[92%] -tw-translate-x-1/2 tw-truncate tw-rounded tw-bg-[#57534E] tw-font-semibold tw-uppercase tw-leading-tight tw-text-white ${badgeCls}`}>
        {badge}
      </span>
    </span>
  );
}

type ChatFileThumbnailProps = {
  fileName: string;
  mimeType: string | null;
  url?: string;
  /** grid = media tab tile (h-28); inline = small icon in message row */
  variant?: "grid" | "inline";
  className?: string;
  /** Overrides the non-image/video glyph size for compact consumers. */
  iconClassName?: string;
};

/** Preview if image/video; otherwise Telegram-style extension badge. */
export function ChatFileThumbnail({ fileName, mimeType, url, variant = "inline", className, iconClassName }: ChatFileThumbnailProps) {
  const kind = chatFileKind(mimeType, fileName);
  const ext = chatFileExtensionLabel(fileName);
  // Fall back to the file glyph when an image fails instead of rendering its name across the card.
  const [broken, setBroken] = React.useState(false);
  const gridCls = "tw-h-full tw-w-full";
  const inlineCls = "tw-h-12 tw-w-12 tw-shrink-0 tw-rounded-[10px]";

  React.useEffect(() => {
    setBroken(false);
  }, [url]);

  if (variant === "grid") {
    if (kind === "image" && url && !broken) {
      return (
        <ChatImagePreviewTrigger src={url} alt={fileName} className="tw-size-full">
          <img src={url} alt="" onError={() => setBroken(true)} className={clsx(gridCls, "tw-object-cover", className)} loading="lazy" crossOrigin="use-credentials" />
        </ChatImagePreviewTrigger>
      );
    }
    if (kind === "video" && url) {
      return (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption -- chat file attachment */}
          <video className={clsx(gridCls, "tw-bg-black tw-object-cover", className)} controls preload="metadata" playsInline src={url} crossOrigin="use-credentials" />
        </>
      );
    }
    return (
      <div className={clsx(gridCls, "tw-flex tw-items-center tw-justify-center tw-bg-[#F5F5F4]", className)} aria-hidden>
        <FileGlyph ext={ext} size="grid" iconClassName={iconClassName} />
      </div>
    );
  }

  if (kind === "image" && url) {
    return (
      <ChatImagePreviewTrigger src={url} alt={fileName}>
        <img src={url} alt="" className={clsx(inlineCls, "tw-object-cover", className)} loading="lazy" crossOrigin="use-credentials" />
      </ChatImagePreviewTrigger>
    );
  }

  if (kind === "video" && url) {
    return (
      <div className={clsx(inlineCls, "tw-relative tw-overflow-hidden tw-bg-black", className)}>
        {/* eslint-disable-next-line jsx-a11y/media-has-caption -- chat file attachment */}
        <video className="tw-size-full tw-object-cover" preload="metadata" muted playsInline src={url} crossOrigin="use-credentials" />
      </div>
    );
  }

  return (
    <div className={clsx(inlineCls, "tw-flex tw-items-center tw-justify-center tw-bg-[#F5F5F4]", className)} aria-hidden>
      <FileGlyph ext={ext} size="inline" />
    </div>
  );
}

/**
 * Files toggle — a single ICON button (frames 5284-117324 / 5289-123835 show icon buttons in the thread
 * header, not text tabs). Highlighted when the Files view is active; clicking toggles chat ⇄ files.
 */
export function ChatThreadTabButtons({ activeTab, onActiveTabChange }: { activeTab: "chat" | "media"; onActiveTabChange: (t: "chat" | "media") => void }) {
  const intl = useIntl();
  const media = activeTab === "media";
  return (
    <button
      type="button"
      aria-pressed={media}
      aria-label={intl.formatMessage({ id: media ? "chat.tab.messages" : "chat.files" })}
      title={intl.formatMessage({ id: "chat.files" })}
      onClick={() => onActiveTabChange(media ? "chat" : "media")}
      className={clsx(
        "tw-inline-flex tw-size-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-lg tw-border-0 tw-transition-colors",
        media ? "tw-bg-[#EFEDEB] tw-text-brand-black" : "tw-bg-transparent tw-text-[#6B7280] hover:tw-bg-gray-50 hover:tw-text-brand-black",
      )}
    >
      <Files className="tw-size-[19px] tw-shrink-0" />
    </button>
  );
}
